import type {
  CampaignPurpose,
  CampaignTemplateVariableConfig,
  CampaignTemplateVariableMapping,
  CampaignTemplateVariableSource,
  WhatsAppTemplate,
} from '@workspace/types';
import {
  COBRANZAS_TEMPLATE_VARIABLE_SOURCES,
  CONTACT_TEMPLATE_VARIABLE_SOURCES,
  extractTemplateVariableIndices,
} from '@workspace/utils';

export function defaultSourceForSlot(
  slot: number,
  purpose: CampaignPurpose,
): CampaignTemplateVariableSource {
  if (slot === 1) {
    return 'contact.firstName';
  }
  if (slot === 2 && purpose === 'COBRANZAS') {
    return 'debt.invoiceNumber';
  }
  return 'contact.firstName';
}

export function buildDefaultTemplateMapping(
  slots: number[],
  purpose: CampaignPurpose,
): CampaignTemplateVariableMapping {
  const mapping: CampaignTemplateVariableMapping = {};
  for (const slot of slots) {
    mapping[String(slot)] = defaultSourceForSlot(slot, purpose);
  }
  return mapping;
}

function allowedSourcesForPurpose(purpose: CampaignPurpose): Set<CampaignTemplateVariableSource> {
  const sources =
    purpose === 'COBRANZAS'
      ? COBRANZAS_TEMPLATE_VARIABLE_SOURCES
      : CONTACT_TEMPLATE_VARIABLE_SOURCES;
  return new Set(sources);
}

export function sanitizeTemplateMappingForPurpose(
  mapping: CampaignTemplateVariableMapping,
  slots: number[],
  purpose: CampaignPurpose,
): CampaignTemplateVariableMapping {
  const defaults = buildDefaultTemplateMapping(slots, purpose);
  const allowed = allowedSourcesForPurpose(purpose);
  const sanitized: CampaignTemplateVariableMapping = { ...defaults };
  for (const slot of slots) {
    const key = String(slot);
    const source = mapping[key];
    if (source && allowed.has(source)) {
      sanitized[key] = source;
    }
  }
  return sanitized;
}

export function mergeTemplateVariableConfig(
  current: CampaignTemplateVariableConfig,
  templateName: string,
  mapping: CampaignTemplateVariableMapping,
): CampaignTemplateVariableConfig {
  const boundedName = templateName.trim().slice(0, 128);
  if (!boundedName) {
    return current;
  }
  return {
    ...current,
    [boundedName]: mapping,
  };
}

export function resolveTemplateSlots(
  templateName: string,
  templates: WhatsAppTemplate[],
  purpose: CampaignPurpose,
): number[] {
  const boundedName = templateName.trim().slice(0, 128);
  const template = templates.find(item => item.name === boundedName);
  if (template) {
    const slots = extractTemplateVariableIndices(template.components);
    if (slots.length > 0) {
      return slots;
    }
  }
  return purpose === 'COBRANZAS' ? [1, 2] : [1];
}

export function ensureTemplateVariableConfig(params: {
  config: CampaignTemplateVariableConfig;
  templateNames: string[];
  templates: WhatsAppTemplate[];
  purpose: CampaignPurpose;
}): CampaignTemplateVariableConfig {
  let next = { ...params.config };
  for (const templateName of params.templateNames) {
    const boundedName = templateName.trim().slice(0, 128);
    if (!boundedName) {
      continue;
    }
    const slots = resolveTemplateSlots(boundedName, params.templates, params.purpose);
    const existing = next[boundedName] ?? {};
    const merged = sanitizeTemplateMappingForPurpose(existing, slots, params.purpose);
    next = mergeTemplateVariableConfig(next, boundedName, merged);
  }
  return next;
}
